import { useState, useEffect } from 'react'

// Reads /api/earnings for the TickerSheet Earnings tab: the reported quarterly
// numbers plus the call transcript, merged per quarter.
//
// Named useEarningsHistory, not useEarnings — `useEarnings` already exists in
// useSupabaseData and returns the forward-looking earnings CALENDAR. These are
// past reported quarters; conflating the two names would be a trap.
//
// Split into two hooks on purpose. Transcript bodies average ~44 KB, so the
// quarter list carries numbers + call metadata only, and the body is fetched
// when a quarter is actually opened. Fusing them would pull ~350 KB to draw
// eight rows.

// Quarter list, newest first. Each entry:
//   { report_date, fiscal_year, fiscal_quarter,
//     transcript: { speakers, paragraph_count, char_count, source } | null,
//     financials: { revenue, gross_profit, operating_income, net_income,
//                   eps_diluted, ebitda, rnd, sga, ... } | null }
// Either half can be null: Yahoo publishes the income statement weeks after the
// call, so the newest quarter usually has a transcript and no numbers yet.
export function useEarningsHistory(ticker) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!ticker) { setData(null); setLoading(false); return }
    let cancelled = false
    setLoading(true)
    fetch(`/api/earnings?ticker=${encodeURIComponent(ticker)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) { setData(d); setLoading(false) } })
      .catch(() => { if (!cancelled) { setData(null); setLoading(false) } })
    return () => { cancelled = true }
  }, [ticker])

  return { quarters: data?.quarters ?? [], loading }
}

// One quarter in full — financials plus the transcript's paragraphs. Pass
// fy/fq null to stay idle; the tab only calls this once a quarter is expanded.
export function useEarningsQuarter(ticker, fy, fq) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!ticker || !fy || !fq) { setData(null); setLoading(false); return }
    let cancelled = false
    setLoading(true)
    fetch(`/api/earnings?ticker=${encodeURIComponent(ticker)}&fy=${fy}&fq=${fq}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) { setData(d); setLoading(false) } })
      .catch(() => { if (!cancelled) { setData(null); setLoading(false) } })
    return () => { cancelled = true }
  }, [ticker, fy, fq])

  return { quarter: data?.quarter ?? null, loading }
}
